import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import apiFetch from "../utils/apiFetch";
import UserContext from "../hooks/UserContext";
import EquipmentTab from "../components/substation/EquipmentTab";
import SchemesTab from "../components/substation/SchemesTab";
import AuditLogModal from "../components/substation/AuditLogModal";
import FieldForm from "../components/substation/FieldForm";
import { ConfirmModal } from "../components/substation/SmModal";
import { EQUIPMENT_CONFIG, EQUIPMENT_TABS } from "../components/substation/equipmentConfig";

const BASE_INFO_FIELDS = [
  { key: "name", label: "Номи", type: "text", required: true },
  { key: "voltage", label: "Кучланиш (кВ)", type: "text" },
  { key: "power_mva", label: "Қувват (МВА)", type: "text" },
  { key: "start_year", label: "Ишга тушган йили", type: "text" },
  { key: "address", label: "Манзил", type: "text" },
  { key: "latitude", label: "Кенглик (latitude)", type: "text" },
  { key: "longitude", label: "Узунлик (longitude)", type: "text" },
];

const INCOMING_BREAKER_FIELDS = [
  { key: "incoming_breaker_6kv_allowed_nominal_current_a", label: "6 кВ кириш ўчиргичи: рухсат этилган номинал токи (A)", type: "number", min: "0", step: "any", inputMode: "decimal" },
  { key: "incoming_breaker_6kv_max_load_current_a", label: "6 кВ кириш ўчиргичи: максимал юклама токи, факт бўйича (A)", type: "number", min: "0", step: "any", inputMode: "decimal" },
  { key: "incoming_breaker_10kv_allowed_nominal_current_a", label: "10 кВ кириш ўчиргичи: рухсат этилган номинал токи (A)", type: "number", min: "0", step: "any", inputMode: "decimal" },
  { key: "incoming_breaker_10kv_max_load_current_a", label: "10 кВ кириш ўчиргичи: максимал юклама токи, факт бўйича (A)", type: "number", min: "0", step: "any", inputMode: "decimal" },
];

const OUTGOING_BREAKER_FIELDS = [
  { key: "outgoing_breaker_6kv_allowed_nominal_current_a", label: "6 кВ чиқиш ўчиргичи: рухсат этилган номинал токи (A)", type: "number", min: "0", step: "any", inputMode: "decimal" },
  { key: "outgoing_breaker_6kv_max_load_current_a", label: "6 кВ чиқиш ўчиргичи: максимал юклама токи, факт бўйича (A)", type: "number", min: "0", step: "any", inputMode: "decimal" },
  { key: "outgoing_breaker_10kv_allowed_nominal_current_a", label: "10 кВ чиқиш ўчиргичи: рухсат этилган номинал токи (A)", type: "number", min: "0", step: "any", inputMode: "decimal" },
  { key: "outgoing_breaker_10kv_max_load_current_a", label: "10 кВ чиқиш ўчиргичи: максимал юклама токи, факт бўйича (A)", type: "number", min: "0", step: "any", inputMode: "decimal" },
];

const SWITCHGEAR_EXPANSION_FIELDS = [
  { key: "switchgear_expansion_6kv_cells", label: "6 кВ ТҚ: кенгайтириш мумкин бўлган ячейкалар сони", type: "number", min: "0", step: "1", inputMode: "numeric" },
  { key: "switchgear_expansion_10kv_cells", label: "10 кВ ТҚ: кенгайтириш мумкин бўлган ячейкалар сони", type: "number", min: "0", step: "1", inputMode: "numeric" },
  { key: "switchgear_expansion_35kv_cells", label: "35 кВ ТҚ: кенгайтириш мумкин бўлган ячейкалар сони", type: "number", min: "0", step: "1", inputMode: "numeric" },
  { key: "switchgear_expansion_110kv_cells", label: "110 кВ ТҚ: кенгайтириш мумкин бўлган ячейкалар сони", type: "number", min: "0", step: "1", inputMode: "numeric" },
  { key: "switchgear_expansion_220kv_cells", label: "220 кВ ТҚ: кенгайтириш мумкин бўлган ячейкалар сони", type: "number", min: "0", step: "1", inputMode: "numeric" },
];

const INFO_FIELD_GROUPS = [
  { title: null, fields: BASE_INFO_FIELDS },
  { title: "6/10 кВ кириш ўчиргичлари", fields: INCOMING_BREAKER_FIELDS },
  { title: "6/10 кВ чиқиш ўчиргичлари", fields: OUTGOING_BREAKER_FIELDS },
  { title: "ТҚ кенгайтириш имконияти", fields: SWITCHGEAR_EXPANSION_FIELDS },
];

const INFO_FIELDS = INFO_FIELD_GROUPS.flatMap((group) =>
  group.fields.map((field) => (group.title ? { ...field, group: group.title } : field))
);

function displayValue(value) {
  if (value === null || value === undefined || value === "") return "—";
  return value;
}

export default function SubstationDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { permissions, user } = useContext(UserContext);
  const isGlobal = user?.is_global_access;

  const canCreate = permissions.includes("SUBSTATION_CREATE");
  const canEdit = permissions.includes("SUBSTATION_EDIT");
  const canDelete = permissions.includes("SUBSTATION_DELETE");

  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("info");
  const [cities, setCities] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [equipData, setEquipData] = useState({});
  const [equipLoading, setEquipLoading] = useState({});
  const loadedTabs = useRef(new Set());

  const buildForm = useCallback(
    (data) => {
      const out = {};
      INFO_FIELDS.forEach((f) => {
        out[f.key] = data?.[f.key] ?? "";
      });
      if (isGlobal) out.cityId = data?.cityId ?? "";
      return out;
    },
    [isGlobal]
  );

  const loadSub = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/substation-management/${id}`);
      if (!res.ok) throw new Error("Подстанция топилмади");
      const data = await res.json();
      setSub(data);
      setForm(buildForm(data));
    } catch (e) {
      console.error(e);
      setSub(null);
    } finally {
      setLoading(false);
    }
  }, [buildForm, id]);

  const loadEquipment = useCallback(
    async (type, force = false) => {
      if (!force && loadedTabs.current.has(type)) return;
      setEquipLoading((prev) => ({ ...prev, [type]: true }));
      try {
        const res = await apiFetch(`/api/substation-management/${id}/equipment/${type}`);
        if (res.ok) {
          const items = await res.json();
          setEquipData((prev) => ({ ...prev, [type]: items }));
          loadedTabs.current.add(type);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setEquipLoading((prev) => ({ ...prev, [type]: false }));
      }
    },
    [id]
  );

  useEffect(() => {
    if (!isGlobal) return;
    async function loadCities() {
      try {
        const res = await apiFetch("/api/city");
        if (res.ok) setCities(await res.json());
      } catch (e) {
        console.error(e);
      }
    }
    loadCities();
  }, [isGlobal]);

  useEffect(() => {
    setEquipData({});
    setEquipLoading({});
    loadedTabs.current = new Set();
    setActiveTab("info");
    setEditMode(false);
    setLoading(true);
    loadSub();
  }, [loadSub]);

  useEffect(() => {
    if (activeTab !== "info" && activeTab !== "schemes" && EQUIPMENT_CONFIG[activeTab]) {
      loadEquipment(activeTab);
    }
  }, [activeTab, loadEquipment]);

  function handleRefreshEquipment() {
    if (activeTab !== "info" && activeTab !== "schemes") {
      loadEquipment(activeTab, true);
    }
    loadSub();
  }

  async function handleSaveInfo() {
    if (!form.name?.trim()) return flash("Номи киритилиши шарт", "error");
    setSaving(true);
    try {
      const res = await apiFetch(`/api/substation-management/${id}`, { method: "PATCH", body: form });
      if (!res.ok) throw new Error((await res.json()).message);
      flash("Сақланди", "success");
      setEditMode(false);
      loadSub();
    } catch (e) {
      flash(e.message, "error");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteSub() {
    try {
      const res = await apiFetch(`/api/substation-management/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error((await res.json()).message);
      navigate("/substations");
    } catch (e) {
      flash(e.message, "error");
    }
    setShowDeleteConfirm(false);
  }

  function flash(msg, type = "info") {
    setNotice({ msg, type });
    setTimeout(() => setNotice(null), 3500);
  }

  const counts = sub?.equipmentCounts || {};
  const schemeCount = sub?.substation_schemes?.length || 0;
  const equipmentTotal = EQUIPMENT_TABS.reduce((acc, t) => acc + (Number(counts[t.key]) || 0), 0);

  const metrics = [
    { label: "Кучланиш", value: sub?.voltage ? `${sub.voltage} кВ` : "—" },
    { label: "Қувват", value: sub?.power_mva ? `${sub.power_mva} МВА` : "—" },
    { label: "Ускуналар", value: equipmentTotal },
    { label: "Схемалар", value: schemeCount },
    { label: "Филиал", value: sub?.city?.name || "—" },
    { label: "Ишга тушган йили", value: sub?.start_year || "—" },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="sm-page sm-page--detail sm-page--substation-detail">
          <div className="sm-detail-state">
            <div className="sm-spinner" />
            <span>Юкланмоқда...</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!sub) {
    return (
      <Layout>
        <div className="sm-page sm-page--detail sm-page--substation-detail">
          <div className="sm-detail-state sm-detail-state--error">
            <strong>Подстанция топилмади</strong>
            <Link to="/substations" className="sm-btn sm-btn--ghost">
              &larr; Рўйхатга қайтиш
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="sm-page sm-page--detail sm-page--substation-detail">
        <Link to="/substations" className="sm-detail-back">
          &larr; Рўйхатга қайтиш
        </Link>

        {notice && <div className={`sm-notice sm-notice--${notice.type}`}>{notice.msg}</div>}

        <section className="sm-detail-hero">
          <div className="sm-detail-hero__main">
            <p className="sm-detail-hero__eyebrow">Подстанция бошқарув панели</p>
            <h1 className="sm-detail-hero__title">{displayValue(sub.name)}</h1>
            <div className="sm-detail-hero__badges">
              {sub.voltage && <span className="sm-badge sm-badge--voltage">{sub.voltage} кВ</span>}
              {sub.power_mva && <span className="sm-badge sm-badge--branch">{sub.power_mva} МВА</span>}
              {sub.city && <span className="sm-badge sm-badge--branch">{sub.city.name}</span>}
            </div>
          </div>
          <div className="sm-detail-hero__actions">
            <button className="sm-btn sm-btn--ghost" onClick={() => setShowAudit(true)}>
              Тарих
            </button>
            {canDelete && (
              <button className="sm-btn sm-btn--danger" onClick={() => setShowDeleteConfirm(true)}>
                Ўчириш
              </button>
            )}
          </div>
        </section>

        <section className="sm-detail-metrics" aria-label="Substation summary">
          {metrics.map((metric) => (
            <div className="sm-detail-metric" key={metric.label}>
              <span>{metric.label}</span>
              <strong>{metric.value}</strong>
            </div>
          ))}
        </section>

        <div className="sm-detail-layout">
          <aside className="sm-detail-nav" aria-label="Substation sections">
            <button className={`sm-detail-nav__item ${activeTab === "info" ? "is-active" : ""}`} onClick={() => setActiveTab("info")}>
              <span>Умумий маълумот</span>
            </button>
            {EQUIPMENT_TABS.map((t) => (
              <button
                key={t.key}
                className={`sm-detail-nav__item ${activeTab === t.key ? "is-active" : ""}`}
                onClick={() => setActiveTab(t.key)}
              >
                <span>{t.label}</span>
                {counts[t.key] > 0 && <b>{counts[t.key]}</b>}
              </button>
            ))}
            <button className={`sm-detail-nav__item ${activeTab === "schemes" ? "is-active" : ""}`} onClick={() => setActiveTab("schemes")}>
              <span>Схемалар</span>
              {schemeCount > 0 && <b>{schemeCount}</b>}
            </button>
          </aside>

          <main className="sm-detail-workspace">
            {activeTab === "info" && (
              <section className="sm-workspace-card">
                <div className="sm-workspace-card__head">
                  <div>
                    <p>Асосий паспорт</p>
                    <h2>Умумий маълумот</h2>
                  </div>
                  {canEdit && !editMode && (
                    <button className="sm-btn sm-btn--primary" onClick={() => setEditMode(true)}>
                      Таҳрирлаш
                    </button>
                  )}
                </div>

                {editMode ? (
                  <>
                    <FieldForm fields={INFO_FIELDS} values={form} onChange={setForm} className="sm-form sm-form--detail" />
                    {isGlobal && (
                      <div className="sm-form sm-form--detail sm-form--city">
                        <div className="sm-field">
                          <label className="sm-field__label">Филиал</label>
                          <select
                            className="sm-field__select"
                            value={form.cityId || ""}
                            onChange={(e) => setForm((p) => ({ ...p, cityId: e.target.value ? Number(e.target.value) : "" }))}
                          >
                            <option value="">Танланг...</option>
                            {cities.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                    <div className="sm-detail-actionbar">
                      <button className="sm-btn sm-btn--ghost" onClick={() => { setEditMode(false); setForm(buildForm(sub)); }}>
                        Бекор
                      </button>
                      <button className="sm-btn sm-btn--primary" onClick={handleSaveInfo} disabled={saving}>
                        {saving ? "Сақланмоқда..." : "Сақлаш"}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="sm-detail-field-grid">
                    {INFO_FIELD_GROUPS.map((group) => (
                      <React.Fragment key={group.title || "base"}>
                        {group.title && <div className="sm-detail-field-group-title">{group.title}</div>}
                        {group.fields.map((f) => (
                          <div key={f.key} className="sm-detail-field">
                            <span>{f.label}</span>
                            <strong>{displayValue(sub[f.key])}</strong>
                          </div>
                        ))}
                      </React.Fragment>
                    ))}
                    <div className="sm-detail-field">
                      <span>Филиал</span>
                      <strong>{displayValue(sub.city?.name)}</strong>
                    </div>
                  </div>
                )}
              </section>
            )}

            {EQUIPMENT_TABS.map((t) =>
              activeTab === t.key ? (
                <section key={t.key} className="sm-workspace-card">
                  {equipLoading[t.key] ? (
                    <div className="sm-detail-state">
                      <div className="sm-spinner" />
                      <span>Юкланмоқда...</span>
                    </div>
                  ) : (
                    <EquipmentTab
                      substationId={sub.id}
                      config={EQUIPMENT_CONFIG[t.key]}
                      items={equipData[t.key] || []}
                      canCreate={canCreate}
                      canEdit={canEdit}
                      canDelete={canDelete}
                      onRefresh={handleRefreshEquipment}
                    />
                  )}
                </section>
              ) : null
            )}

            {activeTab === "schemes" && (
              <section className="sm-workspace-card">
                <SchemesTab
                  substationId={sub.id}
                  schemes={sub.substation_schemes || []}
                  canCreate={canCreate}
                  canDelete={canDelete}
                  onRefresh={loadSub}
                />
              </section>
            )}
          </main>
        </div>

        {showAudit && <AuditLogModal substationId={sub.id} onClose={() => setShowAudit(false)} />}

        {showDeleteConfirm && (
          <ConfirmModal
            title="Подстанцияни ўчириш"
            text={`"${sub.name}" ўчирмоқчимисиз?`}
            subText="Барча маълумотлар аудит-логга сақланади."
            onConfirm={handleDeleteSub}
            onCancel={() => setShowDeleteConfirm(false)}
          />
        )}
      </div>
    </Layout>
  );
}
